import ExcelJS from "exceljs";
import { format, getISOWeek } from "date-fns";
import { COMPANY_NAME, COMPANY_RIF } from "./company.js";
import { normalizeMilkPayment, sortMilkPayments, splitDayDiscount } from "./milkUtils.js";

const DAY_ROWS = [10, 11, 12, 13, 14, 15, 16];

const dateKey = (value) => String(value || "").slice(0, 10);

const fmtObsLiters = (value) => {
  const n = Number(value) || 0;
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
};

const fileSlug = (name) =>
  String(name || "proveedor")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_")
    .replace(/[^\w.-]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "") || "proveedor";

export function milkTypeLabel(litersVaca, litersBufalo) {
  const vaca = Number(litersVaca) > 0;
  const bufalo = Number(litersBufalo) > 0;
  if (vaca && bufalo) return "VACA Y BÚFALA";
  if (bufalo) return "BÚFALA";
  return "VACA";
}

const DAY_NAMES = ["Jueves", "Viernes", "Sábado", "Domingo", "Lunes", "Martes", "Miércoles"];

function formatPayDate(value) {
  const key = dateKey(value);
  if (key.length >= 10) return format(new Date(`${key}T12:00:00`), "dd/MM/yyyy");
  const dt = value ? new Date(value) : null;
  if (!dt || Number.isNaN(dt.getTime())) return "";
  return format(dt, "dd/MM/yyyy");
}

/** Datos calculados de la plantilla Excel, para exportar igual en archivo o imagen. */
export function buildMilkReceiptModel({
  provider,
  weekly,
  bcvRate,
  payments = [],
  daily: dailyOverride,
  prices,
}) {
  if (!provider) throw new Error("Selecciona un proveedor");
  const days = weekly?.days || [];
  const daily = dailyOverride || provider.daily || {};
  const start = dateKey(days[0]?.date || weekly?.startOfWeek);
  const end = dateKey(days[6]?.date || weekly?.endOfWeek);
  const priceCow = Number(prices?.priceCow ?? provider.priceCow) || 0;
  const priceBuffalo = Number(prices?.priceBuffalo ?? provider.priceBuffalo) || 0;
  const rate = Number(bcvRate?.rate ?? bcvRate) || 0;

  let discountAmount = 0;
  let grossVaca = 0;
  let grossBufalo = 0;
  const dayRows = DAY_NAMES.map((name, index) => {
    const day = days[index];
    const key = dateKey(day?.date);
    const cell = (key && daily[key]) || {};
    const vaca = Number(cell.vaca) || 0;
    const bufalo = Number(cell.bufalo) || 0;
    const discount = Number(cell.discount) || 0;
    const split = splitDayDiscount(vaca, bufalo, discount, cell.discountScope);
    grossVaca += vaca;
    grossBufalo += bufalo;
    discountAmount += split.vacaDisc * priceCow + split.bufaloDisc * priceBuffalo;
    return {
      name,
      vaca,
      bufalo,
      total: vaca + bufalo,
      obs: split.discount > 0 ? `Agua ${fmtObsLiters(split.discount)} L` : "",
    };
  });

  const owedVaca = grossVaca * priceCow;
  const owedBufalo = grossBufalo * priceBuffalo;
  const subtotal = owedVaca + owedBufalo;
  const discountUsd = Number(discountAmount.toFixed(2));
  const adicionales = 0;
  const payRows = sortMilkPayments(payments).map((pay) =>
    normalizeMilkPayment(pay, rate),
  );
  const abonosUsd = payRows.reduce((sum, pay) => sum + (Number(pay.usd) || 0), 0);
  const neto = Number((subtotal - discountUsd - abonosUsd + adicionales).toFixed(2));
  const totalBs = rate > 0 ? Number((neto * rate).toFixed(2)) : 0;

  return {
    companyName: COMPANY_NAME,
    companyRif: `RIF ${COMPANY_RIF}`,
    providerName: provider.name || "",
    weekNo: weekNumber(end, start) || "",
    emitDate: end
      ? format(new Date(`${end}T12:00:00`), "dd/MM/yyyy")
      : format(new Date(), "dd/MM/yyyy"),
    milkType: milkTypeLabel(grossVaca, grossBufalo),
    dayRows,
    litersVaca: grossVaca,
    litersBufalo: grossBufalo,
    litersTotal: grossVaca + grossBufalo,
    priceCow,
    priceBuffalo,
    owedVaca,
    owedBufalo,
    subtotal,
    discountUsd,
    discountBs: rate > 0 ? Number((discountUsd * rate).toFixed(2)) : 0,
    adicionales,
    adicionalesBs: 0,
    payments: payRows.map((pay) => ({
      date: formatPayDate(pay.date),
      usd: pay.usd || 0,
      rate: pay.rate || 0,
      bs: pay.bs || 0,
    })),
    rate,
    neto,
    totalBs,
    filenameBase: `leche_${fileSlug(provider.name)}_${start || "semana"}`,
  };
}

function cloneCellStyle(src) {
  return {
    font: { ...(src.font || {}) },
    fill: JSON.parse(JSON.stringify(src.fill || {})),
    border: JSON.parse(JSON.stringify(src.border || {})),
    alignment: { ...(src.alignment || {}) },
    numFmt: src.numFmt,
  };
}

function applyCellStyle(cell, style, numFmt) {
  if (!style) return;
  cell.font = { ...(style.font || {}) };
  if (style.fill && Object.keys(style.fill).length) cell.fill = style.fill;
  if (style.border && Object.keys(style.border).length) cell.border = style.border;
  if (style.alignment && Object.keys(style.alignment).length) cell.alignment = style.alignment;
  cell.numFmt = numFmt || style.numFmt;
}

function snapshotRowStyles(sheet, rowNumber) {
  const row = sheet.getRow(rowNumber);
  return [1, 2, 3, 4, 5].map((col) => cloneCellStyle(row.getCell(col)));
}

function weekNumber(end, start) {
  const key = dateKey(end || start);
  if (key.length < 10) return "";
  return getISOWeek(new Date(`${key}T12:00:00`));
}

function parsePaymentDate(value) {
  const key = dateKey(value);
  if (key.length >= 10) return new Date(`${key}T12:00:00`);
  const dt = value ? new Date(value) : null;
  return dt && !Number.isNaN(dt.getTime()) ? dt : null;
}

function fillAbonos(sheet, payments, fallbackRate) {
  const headerStyles = snapshotRowStyles(sheet, 20);
  const dataStyles = snapshotRowStyles(sheet, 21);
  const rows = sortMilkPayments(payments).map((pay) =>
    normalizeMilkPayment(pay, fallbackRate),
  );
  const count = Math.max(rows.length, 1);
  sheet.spliceRows(25, 0, ...Array.from({ length: count + 1 }, () => []));

  const headerRow = 25;
  const payStart = 26;
  const payEnd = 25 + count;
  const adicRow = payEnd + 1;
  const netoRow = adicRow + 1;
  const tasaRow = netoRow + 1;

  ["Abonos", "Fecha", "Monto", "Tasa", "Monto (Bs.)"].forEach((label, index) => {
    const cell = sheet.getCell(headerRow, index + 1);
    applyCellStyle(cell, headerStyles[index]);
    cell.value = label;
  });

  for (let i = 0; i < count; i += 1) {
    const rowNumber = payStart + i;
    const pay = rows[i];
    const cells = [1, 2, 3, 4, 5].map((col) => sheet.getCell(rowNumber, col));
    cells.forEach((cell, index) => applyCellStyle(cell, dataStyles[index]));
    cells[0].value = null;
    if (!pay) continue;
    const payDate = parsePaymentDate(pay.date);
    cells[1].value = payDate;
    cells[1].numFmt = "dd/mm/yyyy";
    cells[2].value = pay.usd ? Number(pay.usd.toFixed(2)) : null;
    cells[2].numFmt = '"$"#,##0.00';
    cells[3].value = pay.rate ? Number(pay.rate.toFixed(2)) : null;
    cells[3].numFmt = "#,##0.00";
    cells[4].value = pay.bs ? Number(pay.bs.toFixed(2)) : null;
    cells[4].numFmt = "#,##0.00";
  }

  sheet.spliceRows(tasaRow, 1);
  const totalRow = tasaRow;
  const rateFormula = fallbackRate > 0 ? `*${fallbackRate}` : "";

  sheet.getCell("E24").value = rateFormula ? { formula: `D24${rateFormula}` } : null;
  sheet.getCell(`E${adicRow}`).value = rateFormula
    ? { formula: `D${adicRow}${rateFormula}` }
    : null;
  sheet.getCell(`D${netoRow}`).value = {
    formula: `D23-D24-SUM(C${payStart}:C${payEnd})+D${adicRow}`,
  };
  sheet.getCell(`C${totalRow}`).value = null;
  sheet.getCell(`D${totalRow}`).value = null;
  sheet.getCell(`E${totalRow}`).value = rateFormula
    ? { formula: `D${netoRow}${rateFormula}` }
    : null;
  sheet.getCell(`E${totalRow}`).numFmt = "#,##0.00";
}

export async function exportProviderMilkTemplate({
  provider,
  weekly,
  bcvRate,
  payments = [],
  daily: dailyOverride,
  prices,
}) {
  if (!provider) throw new Error("Selecciona un proveedor");
  const days = weekly?.days || [];
  const daily = dailyOverride || provider.daily || {};
  const res = await fetch(`${process.env.PUBLIC_URL || ""}/templates/PlantillaLeche.xlsx`);
  if (!res.ok) throw new Error("No se encontró la plantilla de Excel.");
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await res.arrayBuffer());
  const sheet = workbook.worksheets[0];
  if (!sheet) throw new Error("La plantilla no tiene hojas.");

  const start = dateKey(days[0]?.date || weekly?.startOfWeek);
  const end = dateKey(days[6]?.date || weekly?.endOfWeek);
  const priceCow = Number(prices?.priceCow ?? provider.priceCow) || 0;
  const priceBuffalo = Number(prices?.priceBuffalo ?? provider.priceBuffalo) || 0;
  let discountAmount = 0;
  let grossVaca = 0;
  let grossBufalo = 0;

  DAY_ROWS.forEach((row, index) => {
    const day = days[index];
    const key = dateKey(day?.date);
    const cell = (key && daily[key]) || {};
    const vaca = Number(cell.vaca) || 0;
    const bufalo = Number(cell.bufalo) || 0;
    const discount = Number(cell.discount) || 0;
    const split = splitDayDiscount(vaca, bufalo, discount, cell.discountScope);
    sheet.getCell(`B${row}`).value = vaca || null;
    sheet.getCell(`C${row}`).value = bufalo || null;
    sheet.getCell(`E${row}`).value =
      split.discount > 0 ? `Agua ${fmtObsLiters(split.discount)} L` : null;
    grossVaca += vaca;
    grossBufalo += bufalo;
    discountAmount += split.vacaDisc * priceCow + split.bufaloDisc * priceBuffalo;
  });

  sheet.getCell("B5").value = provider.name || "";
  sheet.getCell("E5").value = weekNumber(end, start) || "";
  sheet.getCell("B6").value = end
    ? format(new Date(`${end}T12:00:00`), "dd/MM/yyyy")
    : format(new Date(), "dd/MM/yyyy");
  sheet.getCell("E6").value = milkTypeLabel(grossVaca, grossBufalo);
  sheet.getCell("C21").value = priceCow;
  sheet.getCell("C22").value = priceBuffalo;
  sheet.getCell("D24").value = Number(discountAmount.toFixed(2));

  const rate = Number(bcvRate?.rate ?? bcvRate) || 0;
  fillAbonos(sheet, payments, rate);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `leche_${fileSlug(provider.name)}_${start || "semana"}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
